import * as React from 'react';
import { Provider } from 'mobx-react';
import { configure } from 'mobx';
import { Environment } from '../../services/Environment';
import { INode } from '../../interfaces/INode';
import { IHeader } from '../../interfaces/IHeader';
import { IInstance } from '../../interfaces/IInstance';
import { TenantSettingsService } from 'intraactive-sdk-helper';
import { ITenantSettings } from '../../interfaces/ITenantSettings';
import { Messages } from '../messages/Messages';
import { MessagesStore } from '../messages/stores';
import DateTimeService from '../../services/DateTimeService';
import { INavigatorContentFromLink } from '../../interfaces/INavigatorContentFromLink';
import { sdkContainerInstance } from '../global';
import { LoggingService } from '../../services/LoggingService';
import { Helper } from '../../Helper';
import { IMessage } from '../../interfaces/IMessage';
import { IFontSlots } from '../../interfaces/IFontSlots';
import { IEventPeriod } from '../../interfaces/IEventPeriod';
import { IMessageDisplayMode } from '../../interfaces/IMessageDisplayMode';
const PACKAGE = require("../../../package.json");

export interface IEventProps {
  instance: IInstance;
  tenant: string;
  token: string;
  webpartId?: string;
  environment?: "Development" | "Test" | "Production";
  highlightColor: string;
  rollupHighlightColor?: string;
  numberOfCards: number;
  shouldFilterByCategory?: boolean;
  selectedCategories?: INode[];
  isTargetingAvailable?: boolean;
  isAdminPage: boolean;
  borderColor?: string;
  borderRadius?: number;
  backgroundColor?: string;
  textColor?: string;
  dateBadgeBackgroundColor?: string;
  dateBadgeTextColor?: string;
  dateBorderColor?: string;
  header?: IHeader;
  showDetailsViewInline?: boolean;
  display?: IMessageDisplayMode;
  noContentMessage?: string;
  commentsEnabled?: boolean;
  likesEnabled?: boolean;
  isNavigator?: boolean;
  openContentFromLink?: INavigatorContentFromLink; // used to open event from link
  showFilter?: boolean;
  showPaging?: boolean;
  hideFullViewCoverOverlay ?: boolean;
  hideFullViewDate ?: boolean;
  calendarHeight?: number | "auto";

  showPeriod?: IEventPeriod;
  showTitle?: boolean;
  showImage?: boolean;
  showLikes?: boolean;
  showComments?: boolean;
  showCategories?: boolean;
  showTimespand?: boolean;
  showLocation?: boolean;
  addMessage?: boolean;
  showDropShadow?: boolean;

  onlyShowIfThereIsContent?: boolean;
  textIfNoContent?: string;
  autoRotate?: boolean;
  showTeaser?: boolean;
  displayDateOnCover?: boolean;
  webpartDisplayMode?: number;
  isHidden?: boolean;
  shouldReQuery?: boolean;
  isMobileApp?: boolean;
  preview?: boolean;
  hasDarkBackground?: boolean;
  maxAmountOfColumns?: number;
  showShowAllButton?: boolean;
  showAllButtonLink?: string;
  fontSlots?: IFontSlots;
  userLocation?: string;

  close?: () => void;
  panelIsOpen?: (panelIsOpen: boolean) => void;
  closePanel?: boolean;
  targetingIsEnabled?: (enabled: boolean) => void;
  isMessagesTemplateEditor?: boolean; // used in admin to edit and create messages templates
  initialJsonTemplate?: string; // used in admin to edit and create messages templates
  saveTemplateCallback?: (jsonTemplate: string) => void;  // used in admin to edit and create messages templates
}

export class IAEvent extends React.Component<IEventProps> {

  private messagesStore: MessagesStore;

  constructor(props: IEventProps) {
    super(props);
    this.messagesStore = new MessagesStore();
    this.messagesStore.showCommandBarItems = true;
    configure({
      isolateGlobalState: true,
      enforceActions: "never"
    });

    this.updateStore(props);

    LoggingService.init(this.props.environment);

    if (this.messagesStore.environment && this.messagesStore.tenant && this.messagesStore.token) {
      const tenantSettings = localStorage.getItem("IA_tenantSettings");
      if (tenantSettings && new DateTimeService().getDatePlusTwoMinuts(new Date(JSON.parse(tenantSettings).date)) > new Date()) {
        console.log("Using cached tenant settings");
        this.messagesStore.tenantSettings = JSON.parse(tenantSettings).value;
      } else {
        console.log("Fetching tenant settings again");
        const tenantSettingsService = new TenantSettingsService<ITenantSettings>(Environment.getEnvironmentForSettingsAPI(this.messagesStore.environment), sdkContainerInstance);
        tenantSettingsService.getSettings(this.messagesStore.tenant, undefined, this.messagesStore.token).then(setting => {
          this.messagesStore.tenantSettings = setting;
          Helper.setLocalStorage("IA_tenantSettings", JSON.stringify({
            date: new Date(),
            value: setting
          }));
        });
      }
    }
  }

  public componentDidMount(): void {
    console.log(`%c IntraActive Events title: ${this.props.header?.title} - running IntraActive SDK-UI version: ${PACKAGE?.version}`, 'background: #1c899d; color: #ffffff');
  }

  public componentWillReceiveProps(props: IEventProps) {
    if (props != this.props) {
      this.updateStore(props);

      if (this.props.instance != props.instance) {
        this.messagesStore.getInstance(true);
      }
      if (
        this.props.instance != props.instance ||
        this.props.numberOfCards != props.numberOfCards ||
        JSON.stringify(this.props.selectedCategories) != JSON.stringify(props.selectedCategories) ||
        this.props.shouldFilterByCategory != props.shouldFilterByCategory ||
        this.props.showPeriod != props.showPeriod
      ) {
        this.messagesStore.reload();
      }
    }
    if (props.closePanel) {
      this.messagesStore.closeMessage();
    }
  }

  private updateStore(props: IEventProps) {
    this.messagesStore.hideFullViewCoverOverlay = props.hideFullViewCoverOverlay;
    this.messagesStore.hideFullViewDate = props.hideFullViewDate;
    this.messagesStore.environment = props.environment;
    this.messagesStore.tenant = props.tenant;
    this.messagesStore.token = props.token;
    this.messagesStore.instance = {
      ...props.instance,
      id: props.instance?.id,
      instanceName: props.instance.instanceName !== undefined || props.instance.instanceName !== "" ? props.instance.instanceName : "Default"
    }
    this.messagesStore.component = "Event";
    this.messagesStore.webpartId = props.webpartId;
    this.messagesStore.highlightColor = props.highlightColor;
    this.messagesStore.rollupHighlightColor = props.rollupHighlightColor;
    this.messagesStore.hasDarkBackground = props.hasDarkBackground;
    this.messagesStore.borderColor = props.borderColor;
    this.messagesStore.borderRadius = props.borderRadius;
    this.messagesStore.title = props.header?.title ?? "";
    this.messagesStore.header = !!props.header?.title;
    this.messagesStore.backgroundColor = props.backgroundColor;
    this.messagesStore.textColor = props.textColor;
    this.messagesStore.dateBadgeBackgroundColor = props.dateBadgeBackgroundColor;
    this.messagesStore.dateBadgeTextColor = props.dateBadgeTextColor;
    this.messagesStore.dateBorderColor = props.dateBorderColor;
    this.messagesStore.numMessages = props.numberOfCards;
    this.messagesStore.shouldFilterByCategory = props.shouldFilterByCategory;
    this.messagesStore.selectedCategories = props.selectedCategories;
    this.messagesStore.preview = props.preview;
    if (props.selectedCategories) {
      this.messagesStore.filterData.selectedCategories = props.selectedCategories;
    }
    this.messagesStore.display = props.display;
    this.messagesStore.showFilter = props.showFilter;
    this.messagesStore.showTitle = props.showTitle;
    this.messagesStore.showImage = props.showImage;
    this.messagesStore.showLikes = props.showLikes;
    this.messagesStore.showComments = props.showComments;
    this.messagesStore.showCategories = props.showCategories;
    this.messagesStore.showTimespand = props.showTimespand;
    this.messagesStore.showLocation = props.showLocation;
    this.messagesStore.addMessage = props.addMessage;
    this.messagesStore.showDropShadow = props.showDropShadow;
    this.messagesStore.showPeriod = props.showPeriod;
    this.messagesStore.showPaging = props.showPaging;
    this.messagesStore.isMobileApp = props.isMobileApp;
    this.messagesStore.isMobile = props.isMobileApp;
    this.messagesStore.isMobile = window.innerWidth < 667;
    this.messagesStore.fontSlots = props.fontSlots;
    this.messagesStore.userLocation = props.userLocation;
    this.messagesStore.isTargetingAvailable = props.isTargetingAvailable;
    this.messagesStore.maxAmountOfColumns = props.maxAmountOfColumns;
    this.messagesStore.calendarHeight = props.calendarHeight;
    if (props.showPeriod === "fullView") {
      this.messagesStore.display = props.showPeriod;
    }
    this.messagesStore.isAdminPage = props.isAdminPage;

    this.messagesStore.isMessagesTemplateEditor = this.props.isMessagesTemplateEditor;
    if (props.isMessagesTemplateEditor) {
      let message: IMessage;
      if (props.initialJsonTemplate) {
        message = JSON.parse(props.initialJsonTemplate);
      } else {
        message = {
          title: ""
        }
      }
      this.messagesStore.messageToEdit = message;
    }

    this.updatePeriode();
    this.messagesStore.webpartDisplayMode = props.webpartDisplayMode;
    this.messagesStore.onlyShowIfThereIsContent = props.onlyShowIfThereIsContent;
    this.messagesStore.textIfNoContent = props.textIfNoContent;
    this.messagesStore.autoRotate = props.autoRotate;
    this.messagesStore.showTeaser = props.showTeaser;
    this.messagesStore.displayDateOnCover = props.displayDateOnCover;
    this.messagesStore.isHidden = props.isHidden;
    this.messagesStore.shouldReQuery = props.shouldReQuery;

    this.messagesStore.isNavigator = props.isNavigator;
    this.messagesStore.openContentFromLink = props.openContentFromLink;
    this.messagesStore.openedFromLink = props.isNavigator && props.openContentFromLink ? true : false;

    this.messagesStore.showShowAllButton = props.showShowAllButton;
    this.messagesStore.showAllButtonLink = props.showAllButtonLink;
  }

  private updatePeriode(): void {
    switch (this.messagesStore.showPeriod) {
      case "day":
        this.messagesStore.startDate = new DateTimeService().getDateAtMidnight(new Date());
        this.messagesStore.endDate = new DateTimeService().getDateJustBeforeMidnight(new Date());
        break;
      case "week":
        let startDate = new Date();
        startDate.setDate(startDate.getDate() - (startDate.getDay() - 1));
        this.messagesStore.startDate = startDate;
        let endDate = new Date(this.messagesStore.startDate);
        endDate.setDate(endDate.getDate() + 6);
        this.messagesStore.endDate = endDate;
        break;
      case "month":
        let date = new Date(), y = date.getFullYear(), m = date.getMonth();
        let firstDay = new Date(y, m, 1);
        let lastDay = new Date(y, m + 1, 0);
        this.messagesStore.startDate = firstDay;
        this.messagesStore.endDate = lastDay;
        break;
      case "upcomming":
        this.messagesStore.startDate = new Date();
        break;
      default:
        break;
    }
  }

  render() {
    return (
      <Provider messagesStore={this.messagesStore}>
        <Messages
          instanceId={this.props.instance?.id}
          close={this.props.close ? () => this.props.close() : undefined}
          panelIsOpen={(panelIsOpen: boolean) => this.props.panelIsOpen(panelIsOpen)}
          targetingIsEnabled={this.props.targetingIsEnabled ? (enabled: boolean) => this.props.targetingIsEnabled(enabled) : undefined}
          saveTemplateCallback={this.props.saveTemplateCallback}
        />
      </Provider>
    )
  }
}